import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type MarketplaceTeamSearchJSON = z.infer<
  typeof MarketplaceTeamSearchSchema.MarketplaceTeamSearchZ
>;

export default class MarketplaceTeamSearchSchema {
  public static readonly MarketplaceTeamSearchZ = z.object({
    team: z.string().optional(),
    query: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    contactPerson: z.string().optional(),
    from: z.number().default(0),
    size: z.number().default(10),
  });

  public static validateJSON(json: unknown): MarketplaceTeamSearchJSON {
    return parseOrThrow(MarketplaceTeamSearchSchema.MarketplaceTeamSearchZ, json, {
      logError: true,
      schemaName: "MarketplaceTeamSearchSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceTeamSearchJSON {
    return MarketplaceTeamSearchSchema.MarketplaceTeamSearchZ.safeParse(json).success;
  }

  public static deserialize(values?: Partial<MarketplaceTeamSearchJSON>): MarketplaceTeamSearchJSON {
    return MarketplaceTeamSearchSchema.validateJSON(values ?? {});
  }

  public static getSearchParams(json: MarketplaceTeamSearchJSON): URLSearchParams {
    const params = new URLSearchParams();

    if (json.team) params.append("team", json.team);
    if (json.query) params.append("query", json.query);
    if (json.name) params.append("name", json.name);
    if (json.description) params.append("description", json.description);
    if (json.contactPerson) params.append("contactPerson", json.contactPerson);
    if (json.from) params.append("from", json.from.toString());
    if (json.size) params.append("size", json.size.toString());

    return params;
  }

  public static getCacheKey(json: MarketplaceTeamSearchJSON): string {
    const params = MarketplaceTeamSearchSchema.getSearchParams(json);
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }
}
